import {
  ChartDimensions,
  ChartSize,
  ChartTheme,
  TooltipVariant,
  DotDatum,
  TwoCategoryData,
  ChartAxesProps,
  MarginProps,
  Statistics,
} from '../../types';
import { Theme, ValueOf } from '@redsift/design-system';
import { ChartContainerProps } from '../ChartContainer';
import { MutableRefObject, ReactElement, ReactNode } from 'react';
import { DotProps } from '../Dot';
import { LegendProps } from '../Legend';
import { SortingMethod } from '../../utils';
import { AnchorProps } from '../DataPoint';

/**
 * Component's labels variant.
 */
export const LineChartLegendVariant = {
  none: 'none',
  externalLabel: 'externalLabel',
  externalLabelValue: 'externalLabelValue',
  externalLabelPercent: 'externalLabelPercent',
  custom: 'custom',
} as const;
export type LineChartLegendVariant = ValueOf<typeof LineChartLegendVariant>;

interface LocaleText {
  emptyChartText: string;
}

export type LineChartDimensions = ChartDimensions & {
  fontSize: number;
};

/**
 * Component props.
 */
export interface LineChartProps extends ChartContainerProps, ChartAxesProps {
  /** Dataset to use to generate the chart. */
  data?: TwoCategoryData;
  /** Dot role. If an onClick is provided, the dots will have the role `button`. For a navigation link, please use `getDotAnchorProps` instead. */
  dotRole?: DotProps['role'];
  /** Component to use if the chart is empty (replacing the default one). */
  emptyComponent?: ReactNode;
  /** Method used to define the anchor props to use when the dot is a navigation link. */
  getDotAnchorProps?: (datum: DotDatum) => AnchorProps;
  /** Method to determine whether a dot is selected or not. */
  isDotSelected?: (datum: DotDatum) => boolean | undefined;
  /** Method to override the data point labels. */
  labelDecorator?: (
    datum: DotDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Method modifying what's displayed within the legend when the legendVariant is "custom". */
  legendDecorator?: (
    datum: DotDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Define whether the labels should be displayed inside or outside the charts and if they should contain raw or percentage values. If set to "custom", use `legendDecorator` to customize it. */
  legendVariant?: LineChartLegendVariant;
  /** Props to forward to the Legend block. Can be used to make the legend selectable. */
  legendProps?: Omit<LegendProps, 'data' | 'ref' | 'variant' | 'width'> & { extraLegendItems?: Statistics };
  /** Labels and texts. */
  localeText?: LocaleText;
  /** Custom margins, used to give more space for axes labels and legend for instance. */
  margins?: MarginProps;
  /** Method to be called on a click on a dot. For a navigation link, please use `getDotAnchorProps` instead. */
  onDotClick?: (datum: DotDatum) => void;
  /** LineChart size. */
  size?: ChartSize | LineChartDimensions;
  /** Define how to sort categories. */
  sortingMethod?: SortingMethod;
  /** Reference to the SVG tag. */
  svgRef?: MutableRefObject<SVGSVGElement>;
  /** Color palette to use. You can choose among the list of available color palette or also choose to use the success/warning/danger theme for which you have to specify which key corresponds to which status. */
  colorTheme?: ChartTheme;
  /** Method modifying what's displayed within the tooltip when the tooltipVariant is "custom".  */
  tooltipDecorator?: (
    data: DotDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Tooltip variant. */
  tooltipVariant?: TooltipVariant;
  /** Theme. */
  theme?: Theme;
}

export type StyledLineChartProps = LineChartProps & {};
